import React, { useState, useEffect } from 'react';
import { RefreshCw, Save, Loader2 } from 'lucide-react';

const THRESHOLD_OPTIONS = [
  { cents: 200, label: '$2' },
  { cents: 500, label: '$5' },
  { cents: 1000, label: '$10' },
  { cents: 2000, label: '$20' },
];

const AMOUNT_OPTIONS = [
  { cents: 1000, label: '$10' },
  { cents: 2000, label: '$20' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
];

const AutoRechargeSettings = ({
  enabled = false,
  threshold = 500,
  amount = 2000,
  onSave,
  isAdmin = false,
  loading = false,
}) => {
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const [localThreshold, setLocalThreshold] = useState(threshold);
  const [localAmount, setLocalAmount] = useState(amount);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalEnabled(enabled);
    setLocalThreshold(threshold);
    setLocalAmount(amount);
  }, [enabled, threshold, amount]);

  useEffect(() => {
    setHasChanges(
      localEnabled !== enabled ||
      localThreshold !== threshold ||
      localAmount !== amount
    );
  }, [localEnabled, localThreshold, localAmount, enabled, threshold, amount]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        autoRecharge: localEnabled,
        autoRechargeThreshold: localThreshold,
        autoRechargeAmount: localAmount,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCents = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-freshroad-black mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-freshroad-teal" />
          Auto-Recharge
        </h3>
        <p className="text-gray-500 text-sm">
          Only the group owner can configure auto-recharge settings.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-freshroad-black flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-freshroad-teal" />
          Auto-Recharge
        </h3>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-freshroad-teal text-white rounded-lg hover:bg-freshroad-teal-dark transition-colors text-sm font-medium disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">Enable Auto-Recharge</p>
            <p className="text-sm text-gray-500">
              Automatically add funds when balance drops below threshold
            </p>
          </div>
          <button
            onClick={() => setLocalEnabled(!localEnabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              localEnabled ? 'bg-freshroad-teal' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                localEnabled ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>

        {localEnabled && (
          <>
            {/* Threshold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recharge when balance falls below
              </label>
              <div className="flex flex-wrap gap-2">
                {THRESHOLD_OPTIONS.map(({ cents, label }) => (
                  <button
                    key={cents}
                    onClick={() => setLocalThreshold(cents)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      localThreshold === cents
                        ? 'border-freshroad-teal bg-freshroad-teal text-white'
                        : 'border-gray-200 hover:border-freshroad-teal text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount to add
              </label>
              <div className="flex flex-wrap gap-2">
                {AMOUNT_OPTIONS.map(({ cents, label }) => (
                  <button
                    key={cents}
                    onClick={() => setLocalAmount(cents)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      localAmount === cents
                        ? 'border-freshroad-teal bg-freshroad-teal text-white'
                        : 'border-gray-200 hover:border-freshroad-teal text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              When your balance drops below <strong>{formatCents(localThreshold)}</strong>,
              we'll automatically charge your default payment method <strong>{formatCents(localAmount)}</strong>.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AutoRechargeSettings;
